import { cleanString, sort, sortByProp } from '@Sage/ssm-utils';

import { FIELD_TYPE_STANDARD, FIELD_TYPE_EXTRA, FIELD_TYPE_TAB, ENTITIES } from 'lib/constants';
import { getFieldList } from 'lib/services/fields';

import { icCheckCircleOutline } from '../icons/icCheckCircleOutline';
const entityLists = [
    'empresas',
    'coferta',
    'contactos',
    'expedientes',
    'loferta',
    'pedidos',
    'productos',
    'gestiones',
    'tblagenda',
    'tblusuarios',
    'tblsalesorders',
    'tblsalesorderslines',
    'tblsurveys',
    'tblsales',
    'viewcontactosfullname',
    'viewusuariosfullname',
];

// Map of child entities to their parent entities for dynamic field configuration
export const PARENT_ENTITY_MAP = {
    [ENTITIES.SALESORDERSLINES.fields]: ENTITIES.SALESORDERS.fields,
};

export const getFilterSchema = (t, columnIDsToHide) => {
    let filters = [
        {
            label: t('cfm_label_db_name'),
            id: 'fieldName',
            fields: [
                {
                    id: 'fieldName',
                    dataType: 'text',
                    inputProps: {
                        placeholder: t('cfm_placeholder_search'),
                    },
                },
            ],
        },
        {
            label: t('cfm_label_field_type'),
            id: 'fieldType',
            fields: [
                {
                    id: 'fieldType',
                    dataType: 'singleValueList',
                    inputProps: {
                        placeholder: t('label_selectone'),
                        options: [
                            {
                                label: t('cfm_label_standard'),
                                value: FIELD_TYPE_STANDARD,
                            },
                            {
                                label: t('cfm_label_custom'),
                                value: FIELD_TYPE_EXTRA,
                            },
                        ],
                    },
                },
            ],
        },
        {
            label: t('cfm_label_tab'),
            id: 'tabId',
            fields: [
                {
                    id: 'tabId',
                    dataType: 'singleValueList',
                    inputProps: {
                        placeholder: t('label_selectone'),
                        options: [],
                    },
                },
            ],
        },
        {
            label: t('cfm_label_readonly'),
            id: 'readOnly',
            fields: [
                {
                    id: 'readOnly',
                    dataType: 'singleValueList',
                    inputProps: {
                        placeholder: t('label_selectone'),
                        options: [
                            {
                                label: t('cfm_label_yes'),
                                value: true,
                            },
                            {
                                label: t('cfm_label_no'),
                                value: false,
                            },
                        ],
                    },
                },
            ],
        },
        {
            label: t('cfm_label_visible'),
            id: 'visible',
            fields: [
                {
                    id: 'visible',
                    dataType: 'singleValueList',
                    inputProps: {
                        placeholder: t('label_selectone'),
                        options: [
                            {
                                label: t('cfm_label_yes'),
                                value: true,
                            },
                            {
                                label: t('cfm_label_no'),
                                value: false,
                            },
                        ],
                    },
                },
            ],
        },
        {
            label: t('cfm_label_mandatory'),
            id: 'mandatory',
            fields: [
                {
                    id: 'mandatory',
                    dataType: 'singleValueList',
                    inputProps: {
                        placeholder: t('label_selectone'),
                        options: [
                            {
                                label: t('cfm_label_yes'),
                                value: true,
                            },
                            {
                                label: t('cfm_label_no'),
                                value: false,
                            },
                        ],
                    },
                },
            ],
        },
        {
            label: t('cfm_label_parent_dynamic_name'),
            id: 'dynamicField',
            fields: [
                {
                    id: 'dynamicField',
                    dataType: 'singleValueList',
                    inputProps: {
                        placeholder: t('label_selectone'),
                        options: [],
                    },
                },
            ],
        },
        {
            label: t('cfm_label_environment'),
            id: 'branches',
            fields: [
                {
                    id: 'branches',
                    dataType: 'singleValueList',
                    inputProps: {
                        placeholder: t('label_selectone'),
                        isMulti: true,
                    },
                },
            ],
        },
        {
            label: t('label_show_duplicates_field'),
            id: 'checkDuplicates',
            fields: [
                {
                    id: 'checkDuplicates',
                    dataType: 'singleValueList',
                    inputProps: {
                        placeholder: t('label_selectone'),
                        options: [
                            {
                                label: t('cfm_label_yes'),
                                value: true,
                            },
                            {
                                label: t('cfm_label_no'),
                                value: false,
                            },
                        ],
                    },
                },
            ],
        },
    ];

    if (columnIDsToHide) {
        filters = filters.filter((filter) => !columnIDsToHide.includes(filter.id));
    }

    return filters;
};

const allowedForCustomValidations = ['Expedientes', 'Empresas', 'Contactos', 'tblAgenda'];

export const getColumns = ({
    t,
    params,
    onEdit,
    getAvatarSvg,
    hasFilters,
    hasGeneralFilter,
    entity,
    permissions,
    overridePermissions,
    isEnabledDuplicateEntities,
    columnIDsToHide,
}) => {
    let columns = [];

    let bigSize = false;

    if (
        columnIDsToHide?.length &&
        columnIDsToHide.includes('customValidation') &&
        columnIDsToHide.includes('fieldName')
    ) {
        bigSize = true;
    }
    if (!hasFilters && !hasGeneralFilter) {
        columns.push({
            colId: 'name',
            field: 'name',
            headerName: t('cfm_label_field_name'),
            cellRenderer: 'textCell',
            cellRendererParams: {
                bold: true,
                hoverLink: true,
                useTooltip: false,
                onClick: ({ data }) => {
                    if (
                        (data.fieldType === FIELD_TYPE_EXTRA &&
                            data.idDb !== -1 &&
                            ((overridePermissions && data?.isEditable) ||
                                (permissions?.update &&
                                    ((data?.isEditable && !permissions?.userUpdate) ||
                                        permissions?.userUpdate)))) ||
                        (data.fieldType === FIELD_TYPE_STANDARD &&
                            data.idDb !== -1 &&
                            permissions?.updateStandard)
                    )
                        onEdit(data);
                },
            },
            rowDrag: params.onRowDrag,
            pinned: 'left',
            width: bigSize ? 400 : 200,
            suppressSizeToFit: true,
            resizable: true,
        });
    } else if (hasGeneralFilter || hasFilters) {
        columns.push({
            colId: 'filteredName',
            field: 'name',
            headerName: t('cfm_label_field_name'),
            cellRenderer: 'textCell',
            cellRendererParams: {
                bold: true,
                hoverLink: true,
                useTooltip: false,
                onClick: ({ data }) => {
                    if (
                        (data.fieldType === FIELD_TYPE_EXTRA &&
                            data.idDb !== -1 &&
                            ((overridePermissions && data?.isEditable) ||
                                (permissions?.update &&
                                    ((data?.isEditable && !permissions?.userUpdate) ||
                                        permissions?.userUpdate)))) ||
                        (data.fieldType === FIELD_TYPE_STANDARD &&
                            data.idDb !== -1 &&
                            permissions?.updateStandard)
                    )
                        onEdit(data);
                },
            },
            rowDrag: params.onRowDrag,
            pinned: 'left',
            width: bigSize ? 400 : 200,
            suppressSizeToFit: true,
            resizable: true,
        });
    }
    columns.push(
        {
            colId: 'fieldName',
            field: 'fieldName',
            headerName: t('cfm_label_db_name'),
            resizable: true,
            cellRenderer: 'textCell',
            cellRendererParams: {
                useTooltip: false,
            },
            width: 200,
            suppressSizeToFit: true,
        },
        {
            colId: 'fieldTypeList',
            field: 'fieldTypeList',
            headerName: t('cfm_label_field_type'),
            resizable: true,
            cellRenderer: 'textCell',
            cellRendererParams: {
                useTooltip: false,
            },
            width: 190,
            suppressSizeToFit: true,
        },
        {
            colId: 'type',
            field: 'type',
            headerName: t('cfm_label_data_type'),
            resizable: true,
            cellRenderer: 'avatarCell',
            cellRendererParams: {
                imageSize: 'small',
                useTooltip: false,
                isSquared: true,
                innerMethods: {
                    getAvatarSvg: getAvatarSvg,
                },
            },
            width: bigSize ? 400 : 180,
            suppressSizeToFit: true,
        },
    );
    allowedForCustomValidations.includes(entity) &&
        columns.push({
            colId: 'customValidation',
            field: 'customValidation',
            headerName: t('cfm_label_validation'),
            headerClass: 'custom-validations__header',
            resizable: true,
            width: 190,
            suppressSizeToFit: true,
        });
    columns.push(
        {
            colId: 'dynamicField',
            field: 'dynamicField',
            headerName: t('cfm_label_parent_dynamic_name'),
            resizable: true,
            cellRenderer: 'textCell',
            cellRendererParams: {
                useTooltip: false,
            },
            width: 190,
            suppressSizeToFit: true,
        },
        {
            colId: 'dynamicFieldValues',
            field: 'dynamicFieldValues',
            headerName: t('cfm_label_parent_dynamic_value'),
            resizable: true,
            cellRenderer: 'textCell',
            cellRendererParams: {
                useTooltip: false,
            },
            width: 190,
            suppressSizeToFit: true,
        },
        {
            colId: 'dependeceField',
            field: 'dependenceField',
            headerName: t('cfm_label_parent_dependency'),
            resizable: true,
            cellRenderer: 'textCell',
            cellRendererParams: {
                useTooltip: false,
            },
            width: 190,
            suppressSizeToFit: true,
        },
        {
            colId: 'readOnly',
            field: 'readOnly',
            headerComponent: 'ReadOnlyHeader',
            headerTooltip: t('cfm_label_readonly'),
            width: 30,
            suppressSizeToFit: true,
            headerClass: 'check-icons',
            resizable: false,
            cellClass: 'check-icons',
            cellRenderer: 'iconCell',
            cellRendererParams: {
                customStyles: {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                },
                otherFields: {
                    icons: {
                        true: { svg: icCheckCircleOutline },
                        false: { svg: icCheckCircleOutline },
                    },
                },
            },
        },
        {
            colId: 'visible',
            field: 'visible',
            headerComponent: 'VisibleHeader',
            headerTooltip: t('cfm_label_visible'),
            width: 30,
            suppressSizeToFit: true,
            headerClass: 'check-icons',
            resizable: false,
            cellClass: 'check-icons',
            cellRenderer: 'iconCell',
            cellRendererParams: {
                customStyles: {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                },
                otherFields: {
                    icons: {
                        true: { svg: icCheckCircleOutline },
                        false: { svg: icCheckCircleOutline },
                    },
                },
            },
        },
        {
            colId: 'mandatory',
            field: 'mandatory',
            headerComponent: 'MandatoryHeader',
            headerTooltip: t('cfm_label_mandatory'),
            width: 30,
            suppressSizeToFit: true,
            headerClass: 'check-icons',
            resizable: false,
            cellClass: 'check-icons',
            cellRenderer: 'iconCell',
            cellRendererParams: {
                customStyles: {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                },
                otherFields: {
                    icons: {
                        true: { svg: icCheckCircleOutline },
                        false: { svg: icCheckCircleOutline },
                    },
                },
            },
        },
    );

    if (isEnabledDuplicateEntities.includes(entity)) {
        columns.push({
            colId: 'checkDuplicates',
            field: 'checkDuplicates',
            headerComponent: 'DuplicatesHeader',
            headerTooltip: t('tooltip_fields_manage_duplicates'),
            headerClass: 'check-icons',
            resizable: false,
            cellClass: 'check-icons',
            cellRenderer: 'iconCell',
            cellRendererParams: {
                customStyles: {
                    paddingLeft: '0px',
                    paddingRight: '0px',
                },
                otherFields: {
                    icons: {
                        true: { svg: icCheckCircleOutline },
                        false: { svg: icCheckCircleOutline },
                    },
                },
            },
        });
    }

    if (columnIDsToHide) {
        columns = columns.filter((column) => !columnIDsToHide.includes(column.colId));
    }

    return columns;
};

const mapperStandardField = (field, t) => ({
    id: `s${field.id}`,
    idDb: field.id,
    name: t(field.description),
    fieldName: field.fieldName,
    cellType: field.type,
    type: t(`cfm_label_type_${field.type}`),
    dynamicField: '',
    dynamicFieldValues: '',
    dependenceField: '',
    readOnly: field.readOnly,
    mandatory: field.mandatory,
    visible: field.visible,
    defaultValue: field.defaultValue,
    order: null,
    valueListRelated: field.valueListRelated,
    fieldType: FIELD_TYPE_STANDARD,
    fieldTypeList: t('cfm_label_standard'),
    checkDuplicates: field.checkDuplicates,
    customValidation:
        field.customValidation && field.customValidation.hasOwnProperty('value')
            ? field.customValidation.value
            : field.customValidation,
});

const mapperTabField = (tab, lang) => {
    const finalLang = lang?.toUpperCase();
    return {
        ...tab,
        id: `t${tab.id}`,
        idDb: tab.id,
        name: `${tab[`description${finalLang}`] || tab.description}`,
        fieldName: '',
        type: '',
        dynamicField: '',
        dynamicFieldValues: '',
        dependenceField: '',
        readOnly: '',
        mandatory: '',
        visible: '',
        defaultValue: '',
        order: tab.order,
        fieldType: FIELD_TYPE_TAB,
    };
};

const mapperExtraField = (ef, lang, t) => {
    const finalLang = lang?.toUpperCase();
    const type = ef.type === 'datetime' && !ef.includeTime ? 'date' : ef.type;

    return {
        id: `e${ef.id}`,
        idDb: ef.id,
        name: `${ef[`description${finalLang}`] || ef.description}`,
        fieldName: ef.fieldName,
        cellType: type,
        type: t(`cfm_label_type_${type}`),
        dynamicField: ef.dynamicField,
        dynamicFieldValues: ef.dynamicFieldValues.map((el) => el.value),
        dependenceField: ef.dependenceField,
        readOnly: ef.readOnly,
        mandatory: ef.mandatory,
        visible: ef.visible,
        defaultValue: ef.defaultValue,
        order: ef.order,
        valueListRelated: ef.valueListRelated,
        fieldType: FIELD_TYPE_EXTRA,
        fieldTypeList: t('cfm_label_custom'),
        branches: ef.branches,
        tabId: ef.tabId,
        checkDuplicates: ef.checkDuplicates,
        isEditable: ef.isEditable,
        customValidation:
            ef.customValidation && ef.customValidation.hasOwnProperty('value')
                ? ef.customValidation.value
                : ef.customValidation,
    };
};

const addTabAndExtraFields = (data, tab, extra, extraOrder, lang, t) => {
    data.push(mapperTabField(tab, lang));

    const tabFields = extra.filter((item) => item.tabId === tab.id);
    if (tabFields.length > 0) {
        const extraFields = tabFields.map((ef) => mapperExtraField(ef, lang, t));
        const sortedExtraFields = sort(extraFields, 'order');
        extraOrder[tab.id] = sortedExtraFields.map((ef) => ef.idDb);
        data.push(...sortedExtraFields);
    }
};

export const getMappedFieldList = (entity, lang, t, customGeneralTab) =>
    getFieldList(entity).then(({ data }) => {
        if (!data) {
            return {};
        }
        const { standard, extra, tab } = data;
        const sortedStandard = sortByProp(standard, 'order');
        const list = sortedStandard.map((sf) => mapperStandardField(sf, t));
        const sortedTabs = sort(tab, 'order');
        const tabsOrder = sortedTabs.map((tab) => tab.id);
        const extraOrder = {};

        // General Tab
        if (extra.find((field) => field.tabId === -1)) {
            const generalTab = {
                id: -1,
                order: 0,
                description: customGeneralTab || t('cfm_label_general_tab'),
            };
            addTabAndExtraFields(list, generalTab, extra, extraOrder, lang, t);
        }

        sortedTabs.forEach((tab) => {
            addTabAndExtraFields(list, tab, extra, extraOrder, lang, t);
        });

        return { fields: data, list, tabsOrder, extraOrder };
    });

export const getFilteredList = (list, values) => {
    return list.filter((item) => {
        let pass = true;

        if (values.name) {
            const fieldName = item?.name?.toLowerCase();
            const filterName = values?.name?.toLowerCase();
            pass = pass && fieldName?.includes(filterName);
        }
        if (values.fieldName) {
            const fieldName = item?.fieldName?.toLowerCase();
            const filterName = values?.fieldName?.toLowerCase();
            pass = pass && fieldName?.includes(filterName);
        }
        if (values.fieldType) {
            pass = pass && values.fieldType.value === item.fieldType;
        }
        if (values.tabId) {
            pass = pass && values.tabId.value === item.tabId;
        }
        if (values.readOnly) {
            pass = pass && values.readOnly.value === item.readOnly;
        }
        if (values.visible) {
            pass = pass && values.visible.value === item.visible;
        }
        if (values.mandatory) {
            pass = pass && values.mandatory.value === item.mandatory;
        }
        if (values.checkDuplicates) {
            pass = pass && values.checkDuplicates.value === item.checkDuplicates;
        }
        if (values.dynamicField) {
            pass = pass && values.dynamicField.value === item.dynamicField;
        }
        if (values.branches && values.branches.length && item.branches && item.branches.length) {
            const hasAllBranches = values.branches.every((branch) =>
                item.branches.map((itemBranch) => itemBranch.id).includes(branch.value),
            );
            pass = pass && hasAllBranches;
        } else if (
            values.branches &&
            values.branches.length &&
            (!item.branches || item.branches.length === 0)
        ) {
            pass = false;
        }
        return pass;
    });
};

export const getTabsOptions = (tabs, t, customGeneralTab) => {
    let generalTab = { label: customGeneralTab || t('cfm_label_general_tab'), value: -1 };
    if (!tabs) return [generalTab];
    let finalTabs = tabs.map((tab) => ({
        label: tab.description,
        value: tab.id,
    }));

    finalTabs = [generalTab, ...finalTabs];
    return finalTabs;
};

export const getDynamicFieldOptions = (list) => {
    if (!list) return [];
    const dynamicFields = list
        .filter((field) => {
            const { fieldType, dynamicField } = field;
            return fieldType === FIELD_TYPE_EXTRA && dynamicField && dynamicField.length > 0;
        })
        .map((f) => f.dynamicField);

    // unique list with Set
    const finalDynamicFields = [...new Set(dynamicFields)].map((val) => ({
        label: val,
        value: val,
    }));

    return sort(finalDynamicFields, 'label');
};

function addDynamicStandardField(name, standardFields, list, t) {
    const field = standardFields?.find(
        (item) => item.fieldName.toLowerCase() === name.toLowerCase(),
    );
    if (field) {
        list.push({
            label: `${t(field.description)} (${field.fieldName})`,
            value: field.fieldName,
            valueListRelated: field.valueListRelated,
            type: field.type,
        });
    }
}

export function formatDynamicFields(fieldName, entity, fields, lang, t, allFieldsData = null) {
    const result = [];
    if (entity === ENTITIES.ACCOUNTS.fields) {
        addDynamicStandardField('idtipo', fields?.standard, result, t);
        addDynamicStandardField('idestadoempresa', fields?.standard, result, t);
    } else if (entity === ENTITIES.ACTIVITIES.fields) {
        addDynamicStandardField('idtipogestion', fields?.standard, result, t);
    } else if (entity === ENTITIES.CALENDAR.fields) {
        addDynamicStandardField('idtipogestion', fields?.standard, result, t);
    } else if (entity === ENTITIES.QUOTES.fields) {
        addDynamicStandardField('idtipooferta', fields?.standard, result, t);
    } else if (entity === ENTITIES.OPPORTUNITIES.fields) {
        addDynamicStandardField('idestadoobra', fields?.standard, result, t);
        addDynamicStandardField('idtipo', fields?.standard, result, t);
    } else if (entity === ENTITIES.CONTACTS.fields) {
        addDynamicStandardField('idtiposcontacto', fields?.standard, result, t);
    } else if (entity === ENTITIES.USERS.fields) {
        addDynamicStandardField('idtipousuario', fields?.standard, result, t);
    } else if (entity === ENTITIES.SALESORDERS.fields) {
        addDynamicStandardField('idEstadoSalesOrder', fields?.standard, result, t);
        addDynamicStandardField('salesOrderType', fields?.standard, result, t);
    } else if (entity === ENTITIES.PRODUCTS.fields) {
        addDynamicStandardField('idfamilia', fields?.standard, result, t);
    } else if (entity === ENTITIES.SALESORDERSLINES.fields) {
        // For SalesOrdersLines, include header fields from SalesOrders
        const parentFields = allFieldsData?.[ENTITIES.SALESORDERS.fields]?.fields;
        if (parentFields?.standard) {
            addDynamicStandardField('idEstadoSalesOrder', parentFields.standard, result, t);
            addDynamicStandardField('salesOrderType', parentFields.standard, result, t);
        }
        // Add extra fields from SalesOrders header that are value list type
        if (parentFields?.extra) {
            parentFields.extra.forEach((ef) => {
                if (
                    (ef.type === 'list' || ef.type === 'multivalue') &&
                    !entityLists.includes((ef.valueListRelated || '').toLowerCase())
                ) {
                    result.push({
                        label: `${ef[`description${lang}`] || ef.description} (${ef.fieldName})`,
                        value: ef.fieldName,
                        valueListRelated: ef.valueListRelated,
                        type: ef.type,
                    });
                }
            });
        }
    }

    let extraFields = [];
    if (fields?.extra) {
        fields?.extra.forEach((ef) => {
            if (
                ef.fieldName !== fieldName &&
                (ef.type === 'list' || ef.type === 'multivalue') &&
                !entityLists.includes((ef.valueListRelated || '').toLowerCase())
            ) {
                extraFields?.push({
                    label: `${ef[`description${lang}`] || ef.description} (${ef.fieldName})`,
                    value: ef.fieldName,
                    valueListRelated: ef.valueListRelated,
                    type: ef.type,
                });
            }
        });
        extraFields = sort(extraFields, 'label');
    }
    return result?.concat(extraFields);
}

export function formatDependenceFields(fieldName, fields, lang, t) {
    const filterFn = (field) =>
        field.fieldName !== fieldName &&
        field.type === 'list' &&
        !entityLists.includes((field.valueListRelated || '').toLowerCase());

    const sfs = fields?.standard.filter(filterFn).map((sf) => ({
        label: `${t(sf.description)} (${sf.fieldName})`,
        value: sf.fieldName,
    }));

    const sortedSfs = sort(sfs, 'label');

    const efs = (fields?.extra || []).filter(filterFn).map((ef) => ({
        label: `${ef[`description${lang}`] || ef.description} (${ef.fieldName})`,
        value: ef.fieldName,
    }));

    const sortedEfs = sort(efs, 'label');

    return sortedSfs?.concat(sortedEfs);
}

export function normalizeFieldName(fieldName, pattern) {
    if (!fieldName) return;
    const patt = new RegExp(pattern || '[^a-zA-Z0-9_]');
    let val = '';

    let name = fieldName?.trim().replace(/\s|-/g, '_');
    name = cleanString(name);

    name = name.split('');
    name.forEach((letter) => {
        if (!patt.test(letter)) {
            val += letter;
        }
    });

    // replace consecutive _
    val = val.replace(/_+/g, '_');

    return val;
}

export function isEntityList(listName) {
    return entityLists.includes((listName || '').toLowerCase());
}

export const ENABLED_DUPLICATE_ENTITIES = ['Empresas', 'Contactos'];

export const DUPLICATES_SECTION_SCHEMA = {
    schemaId: 'checkDuplicatesSection',
    uiType: 'section',
    uiGrid: 'linearVertical',
    label: 'title_mange_duplicates',
    expandable: true,
    isHidden: true,
    composition: [
        {
            schemaId: 'checkDuplicates',
            label: 'label_use_this_field_to_control_duplicates',
            uiType: 'checkbox',
        },
    ],
};

export const DUPLICATES_SECTION_SCHEMA_DISABLED = {
    schemaId: 'checkDuplicatesSection',
    uiType: 'section',
    uiGrid: 'linearVertical',
    label: 'title_mange_duplicates',
    expandable: true,
    isHidden: true,
    composition: [
        {
            schemaId: 'checkDuplicates',
            label: 'label_use_this_field_to_control_duplicates',
            uiType: 'checkbox',
            isReadOnly: true,
        },
        {
            schemaId: 'enableDuplicatesInfo',
            uiType: 'message',
            placeholder: 'label_info_text_how_to_enable_duplicates',
        },
    ],
};
